import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatClock, formatDayLong, isoDayIn } from '../mirror/time';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import type { MirroredAppointment } from '../db/schema';
import type { Appointment } from '../wire';

/**
 * Today — the screen the iPad sits on all day.
 *
 * Two things about it are §8 rather than layout.
 *
 * **The list is the mirror plus the outbox.** A booking the owner just made
 * appears immediately, marked *pending*, whether or not there is a network,
 * because it is read out of the queued intent rather than out of the mirror. The
 * moment it lands the intent is `done`, the real row is in the mirror, and the
 * same line redraws with a server id. There is no reconciliation pass to get
 * wrong: the pending row stops existing exactly when the real one starts.
 *
 * **A pending create is hidden once a matching real row exists.** During the few
 * hundred milliseconds between the server committing and this device settling
 * the intent, an SSE-driven refetch can put the real appointment in the mirror
 * first. Showing both would be a duplicate; picking one on the *display* side is
 * not a merge of records — nothing is written, nothing is combined, and if the
 * two ever genuinely disagreed the mirror's copy is the one shown, because the
 * cloud is authoritative.
 */
export function Today(): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const [booking, setBooking] = useState<BookSheetPrefill | null>(null);

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const today = isoDayIn(timezone, runtime.now());

  const mirrored = useLive(() => runtime.mirror.day(today), [], [today]);
  const pending = useLive(() => runtime.outbox.pending(), []);

  const optimistic = pending
    .map((entry) => entry.optimistic)
    .filter((row): row is Appointment => row !== undefined)
    .filter((row) => isoDayIn(timezone, row.slot.start) === today)
    .filter((row) => !mirrored.some((real) => sameBooking(real, row)));

  const rows: DisplayRow[] = [
    ...mirrored.map((a) => ({ appointment: a, pending: false })),
    ...optimistic.map((a) => ({ appointment: a, pending: true })),
  ].sort((a, b) => a.appointment.slot.start.localeCompare(b.appointment.slot.start));

  return (
    <section className="view">
      <div className="view__header">
        <h1>{formatDayLong(today, timezone)}</h1>
        <button
          type="button"
          className="button button--primary"
          onClick={() => setBooking({ source: 'walk_in' })}
        >
          Walk-in
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="empty">Nothing on the book for today.</p>
      ) : (
        <ul className="day-list">
          {rows.map((row) => (
            <li
              key={row.appointment.appointment_id}
              className={`day-row day-row--${row.appointment.status}${row.pending ? ' day-row--pending' : ''}`}
            >
              <div className="day-row__time">
                <strong>{formatClock(row.appointment.slot.start, timezone)}</strong>
                <small>{row.appointment.duration_min} min</small>
              </div>

              <div className="day-row__who">
                <strong>{row.appointment.client.display_name}</strong>
                <small>
                  {row.appointment.service_name}
                  {row.appointment.resource_name === null
                    ? ''
                    : ` · ${row.appointment.resource_name}`}
                </small>
                {row.appointment.notes === null ? null : (
                  <small className="muted">{row.appointment.notes}</small>
                )}
              </div>

              <div className="day-row__tail">
                {row.pending ? (
                  <span className="badge badge--pending">waiting to send</span>
                ) : (
                  <span className={`badge badge--source-${row.appointment.source}`}>
                    {sourceWord(row.appointment.source)}
                  </span>
                )}
                {!row.pending && row.appointment.status === 'confirmed' ? (
                  <div className="row-actions">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        void actions.setStatus({
                          appointmentId: row.appointment.appointment_id,
                          status: 'completed',
                          clientName: row.appointment.client.display_name,
                          whenIso: row.appointment.slot.start,
                        })
                      }
                    >
                      Done
                    </button>
                    <button
                      type="button"
                      className="link-button"
                      onClick={() =>
                        void actions.setStatus({
                          appointmentId: row.appointment.appointment_id,
                          status: 'no_show',
                          clientName: row.appointment.client.display_name,
                          whenIso: row.appointment.slot.start,
                        })
                      }
                    >
                      No-show
                    </button>
                    <button
                      type="button"
                      className="link-button link-button--danger"
                      onClick={() =>
                        void actions.cancel({
                          appointmentId: row.appointment.appointment_id,
                          clientName: row.appointment.client.display_name,
                          whenIso: row.appointment.slot.start,
                        })
                      }
                    >
                      Cancel
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {booking !== null ? (
        <BookSheet
          prefill={booking}
          onDone={() => setBooking(null)}
          onCancel={() => setBooking(null)}
        />
      ) : null}
    </section>
  );
}

interface DisplayRow {
  appointment: Appointment | MirroredAppointment;
  pending: boolean;
}

/**
 * Same claim on the same room at the same instant — which is exactly the
 * comparison the exclusion constraint makes, so if this says "same" the server
 * agrees.
 */
function sameBooking(real: Appointment, optimistic: Appointment): boolean {
  return (
    real.slot.resource_id === optimistic.slot.resource_id &&
    real.slot.start === optimistic.slot.start &&
    real.service_id === optimistic.service_id
  );
}

export function sourceWord(source: string): string {
  switch (source) {
    case 'partner':
      return 'concierge';
    case 'walk_in':
      return 'walk-in';
    case 'import':
      return 'imported';
    default:
      return 'counter';
  }
}
